import { Dayjs } from "utils/dayjs"

import { IApiLink } from "@/types"
import { AssetOwnershipType, AssetStatus } from "models/Asset"
import LmidAsset from "models/LmidAsset"
import ValidationService, { validationRules } from "services/ValidationService"
import i18n from "intl/i18n"
import {
    LmidModalMode,
    CopyAssetSourceKind,
    allowedLmidModes,
    allowedStatuses,
    lmidModePrefill,
    statusPrefill,
} from "./copyAssetMatrix"

export const COPY_ASSET_MAX_ROWS = 30

/**
 * Values shared by all copies. A copy that has no own value for a key inherits
 * the value from here; editing it on a single copy turns it into an override.
 */
export interface ICopyAssetSharedValues {
    status: AssetStatus
    internalOrderNumber: string
    supplierOrderNo: string
    constructionYear: string
    referenceNumberSale: string
    referenceNumberRental: string
    internalNotes: string
    availabilityDate: Dayjs | null
    monthlyRentalCostValue: string
    referenceValue: string
    ownerName: string
}

export type CopyAssetSharedKey = keyof ICopyAssetSharedValues

export const COPY_ASSET_SHARED_KEYS: CopyAssetSharedKey[] = [
    "status",
    "internalOrderNumber",
    "supplierOrderNo",
    "constructionYear",
    "referenceNumberSale",
    "referenceNumberRental",
    "internalNotes",
    "availabilityDate",
    "monthlyRentalCostValue",
    "referenceValue",
    "ownerName",
]

/**
 * Data taken over from an existing asset when its LMID was assigned to a copy.
 * It replaces the corresponding source values for that copy only: the three
 * identification scalars always, plus the full technical block when a tenant
 * asset was selected.
 */
export interface ICopyAssetLmidOverwrite {
    drawingNo?: string | null
    serialNumber?: string | null
    constructionYear?: number | null
    assetData?: LmidAsset | null
}

export interface ICopyAssetRow {
    id: string
    lmidMode: LmidModalMode
    lmid: string
    createAssetLink: IApiLink | null
    lmidOverwrite: ICopyAssetLmidOverwrite | null
    serialNumber: string
    serialNumberMachine: string
    inventoryNo: string
    overrides: Partial<ICopyAssetSharedValues>
}

export interface ICopyAssetState {
    sourceKind: CopyAssetSourceKind
    ownershipType: AssetOwnershipType
    sourceOwnershipType: AssetOwnershipType
    /** Initial condition chosen in the ownership step, applied to every copy. */
    isSecondhand: boolean
    shared: ICopyAssetSharedValues
    rows: ICopyAssetRow[]
    activeRowId: string
    nextRowSeq: number
}

/**
 * Whether the copies need the lending company entered in the dialog: a borrowed
 * copy of a purchased source has no owner to take over, while a borrowed source
 * passes its own lending company on to the copies.
 */
export function needsOwnerInput(state: Pick<ICopyAssetState, "ownershipType" | "sourceOwnershipType">): boolean {
    return state.ownershipType === AssetOwnershipType.borrowed
        && state.sourceOwnershipType === AssetOwnershipType.purchased
}

export enum CopyAssetErrorField {
    lmid = "lmid",
    internalOrderNumber = "internalOrderNumber",
    constructionYear = "constructionYear",
    referenceNumberSale = "referenceNumberSale",
    referenceNumberRental = "referenceNumberRental",
    monthlyRentalCostValue = "monthlyRentalCostValue",
    referenceValue = "referenceValue",
    ownerName = "ownerName",
}

export interface ICopyAssetRowError {
    field: CopyAssetErrorField
    message: string
    /** 1-based number of the copy that already uses the LMID, for duplicates. */
    duplicateOfCopyNumber?: number
}

/** Effective value of a shared key for a copy: its own override, else the shared value. */
export function effectiveValue<K extends CopyAssetSharedKey>(
    state: ICopyAssetState,
    row: ICopyAssetRow,
    key: K,
): ICopyAssetSharedValues[K] {
    const override = row.overrides[key]
    return override !== undefined ? override as ICopyAssetSharedValues[K] : state.shared[key]
}

/** Whether two values of a shared key count as the same, so no override is needed. */
function sharedValuesEqual(
    key: CopyAssetSharedKey,
    a: ICopyAssetSharedValues[CopyAssetSharedKey],
    b: ICopyAssetSharedValues[CopyAssetSharedKey],
): boolean {
    if (key === "availabilityDate") {
        const dateA = a as Dayjs | null
        const dateB = b as Dayjs | null
        if (!dateA || !dateB) return dateA === dateB
        return dateA.isSame(dateB)
    }
    return a === b
}

/** Drops every row override of a key that equals the given shared value. */
function dropRedundantOverrides(
    rows: ICopyAssetRow[],
    key: CopyAssetSharedKey,
    sharedValue: ICopyAssetSharedValues[CopyAssetSharedKey],
): ICopyAssetRow[] {
    return rows.map(row => {
        const override = row.overrides[key]
        if (override === undefined || !sharedValuesEqual(key, override, sharedValue)) return row
        const overrides = { ...row.overrides }
        delete overrides[key]
        return { ...row, overrides }
    })
}

function isBlank(value: unknown): boolean {
    return value === null || value === undefined || String(value).trim() === ""
}

/**
 * Blocking problems of a single copy, each with the message to show at its field.
 *
 * Field formats reuse the add-asset validation rules, so a value the add flow
 * rejects is rejected here too. Beyond that, the checks mirror the create
 * endpoint's own rejections — equal sale and rental reference numbers, and a
 * missing monthly rental cost for a borrowed asset — so a copy can never be
 * confirmed for a reason the server would only reveal afterwards.
 */
export function rowErrors(state: ICopyAssetState, row: ICopyAssetRow): ICopyAssetRowError[] {
    const errors: ICopyAssetRowError[] = []

    if (row.lmidMode === LmidModalMode.enterLmid && isBlank(row.lmid)) {
        errors.push({
            field: CopyAssetErrorField.lmid,
            message: i18n.t('copyAsset.enterLmidOrChooseAnother'),
        })
    }

    const duplicateIndex = duplicateLmidRowIndex(state, row)
    if (duplicateIndex !== null) {
        errors.push({
            field: CopyAssetErrorField.lmid,
            message: i18n.t('copyAsset.lmidDuplicateInDialog', { copyNumber: duplicateIndex + 1 }),
            duplicateOfCopyNumber: duplicateIndex + 1,
        })
    }

    if (row.lmidMode === LmidModalMode.enterLmidLater) {
        const internalOrderNumber = effectiveValue(state, row, "internalOrderNumber")
        if (isBlank(internalOrderNumber)) {
            errors.push({
                field: CopyAssetErrorField.internalOrderNumber,
                message: i18n.t('copyAsset.requiredWhenLmidLater'),
            })
        } else {
            const formatError = ValidationService.validate({ type: "alphanumeric" }, internalOrderNumber)
            if (formatError) {
                errors.push({ field: CopyAssetErrorField.internalOrderNumber, message: formatError.message })
            }
        }
    }

    const constructionYearError = ValidationService.validate(
        validationRules.asset.constructionYear,
        effectiveValue(state, row, "constructionYear"),
    )
    if (constructionYearError) {
        errors.push({ field: CopyAssetErrorField.constructionYear, message: constructionYearError.message })
    }

    const referenceNumberSale = effectiveValue(state, row, "referenceNumberSale")
    const referenceNumberRental = effectiveValue(state, row, "referenceNumberRental")

    const referenceNumberSaleError = ValidationService.validate(
        validationRules.asset.referenceNumberSale,
        referenceNumberSale,
    )
    if (referenceNumberSaleError) {
        errors.push({ field: CopyAssetErrorField.referenceNumberSale, message: referenceNumberSaleError.message })
    }

    if (!isBlank(referenceNumberSale) && !isBlank(referenceNumberRental)) {
        const referenceNumberRentalError = ValidationService.validate(
            { notEqual: String(referenceNumberSale).trim() },
            String(referenceNumberRental).trim(),
        )
        if (referenceNumberRentalError) {
            errors.push({ field: CopyAssetErrorField.referenceNumberRental, message: referenceNumberRentalError.message })
        }
    }

    if (state.ownershipType === AssetOwnershipType.borrowed) {
        const monthlyRentalCostError = ValidationService.validate(
            { required: true, type: "decimal", gte: 0 },
            effectiveValue(state, row, "monthlyRentalCostValue"),
        )
        if (monthlyRentalCostError) {
            errors.push({ field: CopyAssetErrorField.monthlyRentalCostValue, message: monthlyRentalCostError.message })
        }
    } else {
        const referenceValueError = ValidationService.validate(
            validationRules.asset.referenceValue,
            effectiveValue(state, row, "referenceValue"),
        )
        if (referenceValueError) {
            errors.push({ field: CopyAssetErrorField.referenceValue, message: referenceValueError.message })
        }
    }

    if (needsOwnerInput(state)) {
        const ownerNameError = ValidationService.validate({ required: true }, effectiveValue(state, row, "ownerName"))
        if (ownerNameError) {
            errors.push({ field: CopyAssetErrorField.ownerName, message: ownerNameError.message })
        }
    }

    return errors
}

/** Index of the earlier copy already using this copy's manually entered LMID. */
function duplicateLmidRowIndex(state: ICopyAssetState, row: ICopyAssetRow): number | null {
    if (row.lmidMode !== LmidModalMode.enterLmid) return null
    const lmid = row.lmid.trim()
    if (!lmid) return null

    const firstIndex = state.rows.findIndex(candidate =>
        candidate.lmidMode === LmidModalMode.enterLmid
        && candidate.lmid.trim() === lmid)

    const ownIndex = state.rows.findIndex(candidate => candidate.id === row.id)
    return firstIndex !== -1 && firstIndex !== ownIndex ? firstIndex : null
}

/** Copies without any blocking problem. */
export function readyRowCount(state: ICopyAssetState): number {
    return state.rows.filter(row => rowErrors(state, row).length === 0).length
}

/** Id of the first copy that still needs attention, for the jump action. */
export function firstInvalidRowId(state: ICopyAssetState): string | null {
    return state.rows.find(row => rowErrors(state, row).length > 0)?.id ?? null
}

/** How many copies override a given shared key, for the panel indicators. */
export function overrideCount(state: ICopyAssetState, key: CopyAssetSharedKey): number {
    return state.rows.filter(row => row.overrides[key] !== undefined).length
}

/** How many copies override at least one shared key. */
export function editedRowCount(state: ICopyAssetState): number {
    return state.rows.filter(row => Object.keys(row.overrides).length > 0).length
}

export function canAddRow(state: ICopyAssetState): boolean {
    return state.rows.length < COPY_ASSET_MAX_ROWS
}

export function canRemoveRow(state: ICopyAssetState): boolean {
    return state.rows.length > 1
}

function makeRow(state: Pick<ICopyAssetState, "sourceKind" | "ownershipType" | "shared">, seq: number): ICopyAssetRow {
    return {
        id: `copy-${seq}`,
        lmidMode: lmidModePrefill(state.sourceKind, state.ownershipType, state.shared.status),
        lmid: "",
        createAssetLink: null,
        lmidOverwrite: null,
        serialNumber: "",
        serialNumberMachine: "",
        inventoryNo: "",
        overrides: {},
    }
}

export interface ICopyAssetSourceValues {
    status: AssetStatus
    internalOrderNumber?: string | null
    supplierOrderNo?: string | null
    constructionYear?: number | null
    referenceNumberSale?: string | null
    referenceNumberRental?: string | null
    internalNotes?: string | null
    serialNumber?: string | null
    serialNumberMachine?: string | null
    inventoryNo?: string | null
    monthlyRentalCostValue?: string | null
    referenceValue?: string | null
}

/**
 * Initial state of the copy dialog: one copy prefilled from the source, with the
 * source's values as the shared defaults.
 */
export function createInitialState({
    sourceKind,
    ownershipType,
    sourceOwnershipType,
    isSecondhand,
    source,
    today,
    rowCount = 1,
}: {
    sourceKind: CopyAssetSourceKind
    ownershipType: AssetOwnershipType
    sourceOwnershipType: AssetOwnershipType
    isSecondhand: boolean
    source: ICopyAssetSourceValues
    today: Dayjs
    rowCount?: number
}): ICopyAssetState {
    const shared: ICopyAssetSharedValues = {
        status: statusPrefill(source.status, ownershipType),
        internalOrderNumber: source.internalOrderNumber ?? "",
        supplierOrderNo: source.supplierOrderNo ?? "",
        constructionYear: source.constructionYear != null ? String(source.constructionYear) : "",
        referenceNumberSale: source.referenceNumberSale ?? "",
        referenceNumberRental: source.referenceNumberRental ?? "",
        internalNotes: source.internalNotes ?? "",
        availabilityDate: today,
        monthlyRentalCostValue: source.monthlyRentalCostValue ?? "",
        referenceValue: source.referenceValue ?? "",
        ownerName: "",
    }

    const base = { sourceKind, ownershipType, shared }
    const rows = Array.from({ length: Math.max(1, Math.min(COPY_ASSET_MAX_ROWS, rowCount)) },
        (_, index) => ({
            ...makeRow(base, index + 1),
            serialNumber: source.serialNumber ?? "",
            serialNumberMachine: source.serialNumberMachine ?? "",
            inventoryNo: source.inventoryNo ?? "",
        }))

    return {
        sourceKind,
        ownershipType,
        sourceOwnershipType,
        isSecondhand,
        shared,
        rows,
        activeRowId: rows[0].id,
        nextRowSeq: rows.length + 1,
    }
}

type SharedValueAction<T extends string> = {
    [K in CopyAssetSharedKey]: { type: T, id: string, key: K, value: ICopyAssetSharedValues[K] }
}[CopyAssetSharedKey]

type SetSharedAction = {
    [K in CopyAssetSharedKey]: { type: "setShared", key: K, value: ICopyAssetSharedValues[K] }
}[CopyAssetSharedKey]

export type CopyAssetAction =
    | { type: "selectRow", id: string }
    | { type: "addRow", source: ICopyAssetSourceValues }
    | { type: "duplicateRow", id: string }
    | { type: "removeRow", id: string }
    | { type: "setRowValue", id: string, key: "serialNumber" | "serialNumberMachine" | "inventoryNo", value: string }
    | SharedValueAction<"setRowOverride">
    | { type: "clearRowOverride", id: string, key: CopyAssetSharedKey }
    | SetSharedAction
    | { type: "setLmidMode", id: string, lmidMode: LmidModalMode }
    | { type: "assignLmid", id: string, lmid: string, createAssetLink: IApiLink | null, overwrite: ICopyAssetLmidOverwrite | null }

/**
 * Keeps a copy's LMID mode valid for its effective status.
 *
 * The two dropdowns constrain each other, so whenever one changes the other may
 * hold a value that is no longer allowed. An invalid mode falls back to the
 * default rather than leaving the copy in a state the server would reject.
 */
function reconcileLmidMode(state: ICopyAssetState, row: ICopyAssetRow): ICopyAssetRow {
    const status = effectiveValue(state, row, "status")
    const modes = allowedLmidModes(state.sourceKind, state.ownershipType, status)
    if (modes.includes(row.lmidMode)) return row

    const lmidMode = lmidModePrefill(state.sourceKind, state.ownershipType, status)
    return {
        ...row,
        lmidMode,
        lmid: lmidMode === LmidModalMode.enterLmid ? row.lmid : "",
        createAssetLink: lmidMode === LmidModalMode.enterLmid ? row.createAssetLink : null,
    }
}

/**
 * Keeps a copy's status valid for its LMID mode, overriding it when necessary.
 *
 * Only called for modes the source actually supports — a mode with no valid
 * status at all is rejected before it reaches the state.
 */
function reconcileStatus(state: ICopyAssetState, row: ICopyAssetRow): ICopyAssetRow {
    const status = effectiveValue(state, row, "status")
    const statuses = allowedStatuses(state.sourceKind, state.ownershipType, row.lmidMode)
    if (statuses.includes(status)) return row

    const fallback = statuses[0]
    if (fallback === undefined) return row

    if (state.shared.status === fallback) {
        const overrides = { ...row.overrides }
        delete overrides.status
        return { ...row, overrides }
    }

    return { ...row, overrides: { ...row.overrides, status: fallback } }
}

function mapRow(state: ICopyAssetState, id: string, map: (row: ICopyAssetRow) => ICopyAssetRow): ICopyAssetState {
    return { ...state, rows: state.rows.map(row => (row.id === id ? map(row) : row)) }
}

export function copyAssetRowsReducer(state: ICopyAssetState, action: CopyAssetAction): ICopyAssetState {
    switch (action.type) {
        case "selectRow":
            return { ...state, activeRowId: action.id }

        case "addRow": {
            if (!canAddRow(state)) return state
            const row: ICopyAssetRow = {
                ...makeRow(state, state.nextRowSeq),
                serialNumber: action.source.serialNumber ?? "",
                serialNumberMachine: action.source.serialNumberMachine ?? "",
                inventoryNo: action.source.inventoryNo ?? "",
            }
            return {
                ...state,
                rows: [...state.rows, row],
                nextRowSeq: state.nextRowSeq + 1,
            }
        }

        case "duplicateRow": {
            if (!canAddRow(state)) return state
            const index = state.rows.findIndex(row => row.id === action.id)
            if (index === -1) return state
            const original = state.rows[index]
            const isManual = original.lmidMode === LmidModalMode.enterLmid
            const clone: ICopyAssetRow = {
                ...original,
                id: `copy-${state.nextRowSeq}`,
                overrides: { ...original.overrides },
                lmid: isManual ? "" : original.lmid,
                createAssetLink: isManual ? null : original.createAssetLink,
                lmidOverwrite: isManual ? null : original.lmidOverwrite,
            }
            const rows = [...state.rows]
            rows.splice(index + 1, 0, clone)
            return { ...state, rows, nextRowSeq: state.nextRowSeq + 1 }
        }

        case "removeRow": {
            if (!canRemoveRow(state)) return state
            const index = state.rows.findIndex(row => row.id === action.id)
            if (index === -1) return state
            const rows = state.rows.filter(row => row.id !== action.id)
            const activeRowId = state.activeRowId === action.id
                ? rows[Math.min(index, rows.length - 1)].id
                : state.activeRowId
            return { ...state, rows, activeRowId }
        }

        case "setRowValue":
            return mapRow(state, action.id, row => ({ ...row, [action.key]: action.value }))

        case "setRowOverride": {
            const next = mapRow(state, action.id, row => {
                const overrides = { ...row.overrides }
                if (sharedValuesEqual(action.key, action.value, state.shared[action.key])) {
                    delete overrides[action.key]
                } else {
                    Object.assign(overrides, { [action.key]: action.value })
                }
                return { ...row, overrides }
            })
            return action.key === "status"
                ? mapRow(next, action.id, row => reconcileLmidMode(next, row))
                : next
        }

        case "clearRowOverride": {
            const next = mapRow(state, action.id, row => {
                const overrides = { ...row.overrides }
                delete overrides[action.key]
                return { ...row, overrides }
            })
            return action.key === "status"
                ? mapRow(next, action.id, row => reconcileLmidMode(next, row))
                : next
        }

        case "setShared": {
            const shared = { ...state.shared, [action.key]: action.value }
            const rows = dropRedundantOverrides(state.rows, action.key, action.value)
            const withShared: ICopyAssetState = { ...state, shared, rows }
            if (action.key !== "status") return withShared
            return { ...withShared, rows: withShared.rows.map(row => reconcileLmidMode(withShared, row)) }
        }

        case "setLmidMode": {
            const reachableStatuses = allowedStatuses(state.sourceKind, state.ownershipType, action.lmidMode)
            if (reachableStatuses.length === 0) return state

            const withMode = mapRow(state, action.id, row => ({
                ...row,
                lmidMode: action.lmidMode,
                lmid: action.lmidMode === LmidModalMode.enterLmid ? row.lmid : "",
                createAssetLink: action.lmidMode === LmidModalMode.enterLmid ? row.createAssetLink : null,
                lmidOverwrite: action.lmidMode === LmidModalMode.enterLmid ? row.lmidOverwrite : null,
            }))
            return mapRow(withMode, action.id, row => reconcileStatus(withMode, row))
        }

        case "assignLmid":
            return mapRow(state, action.id, row => {
                const overwrite = action.overwrite
                const next: ICopyAssetRow = {
                    ...row,
                    lmid: action.lmid,
                    createAssetLink: action.createAssetLink,
                    lmidOverwrite: overwrite,
                }
                if (overwrite && "serialNumber" in overwrite) {
                    next.serialNumber = overwrite.serialNumber ?? ""
                }
                if (overwrite && "constructionYear" in overwrite) {
                    const constructionYear = overwrite.constructionYear != null
                        ? String(overwrite.constructionYear)
                        : ""
                    next.overrides = constructionYear === state.shared.constructionYear
                        ? (() => {
                            const overrides = { ...next.overrides }
                            delete overrides.constructionYear
                            return overrides
                        })()
                        : { ...next.overrides, constructionYear }
                }
                return next
            })

        default:
            return state
    }
}
